import graphql from "babel-plugin-relay/macro";
import React, { createContext, useMemo } from "react";
import { useFragment } from "react-relay";

import { ClubCurrencyContextFragment$key } from "./__generated__/ClubCurrencyContextFragment.graphql";
import { ClubCurrencyContextUnauthenticatedFragment$key } from "./__generated__/ClubCurrencyContextUnauthenticatedFragment.graphql";
import {
  DEFAULT_CURRENCY_CODE,
  convertCurrencyCodeToSymbol,
} from "./convertCurrencyCodeToSymbol";

type ClubCurrencyContextValue = {
  currencyCode: string;
  currencySymbol: string;
};

export const ClubCurrencyContext = createContext<
  ClubCurrencyContextValue | undefined
>(undefined);

export const ClubCurrencyProvider: React.FC<{
  children: React.ReactNode;
  clubFragmentRef?: ClubCurrencyContextFragment$key | null;
  unauthenticatedClubFragmentRef?: ClubCurrencyContextUnauthenticatedFragment$key | null;
}> = ({
  children,
  clubFragmentRef = null,
  unauthenticatedClubFragmentRef = null,
}) => {
  const clubData = useFragment(ClubCurrencyContextFragment, clubFragmentRef);
  const unauthenticatedClubData = useFragment(
    ClubCurrencyContextUnauthenticatedFragment,
    unauthenticatedClubFragmentRef
  );

  const currency = clubData?.currency ?? unauthenticatedClubData?.currency;

  const value = useMemo(() => {
    const currencyCode =
      typeof currency === "string"
        ? currency.toLowerCase()
        : DEFAULT_CURRENCY_CODE;

    const currencySymbol = convertCurrencyCodeToSymbol(currency);

    return { currencyCode, currencySymbol };
  }, [currency]);

  return (
    <ClubCurrencyContext.Provider value={value}>
      {children}
    </ClubCurrencyContext.Provider>
  );
};

export const ClubCurrencyContextFragment = graphql`
  fragment ClubCurrencyContextFragment on Club {
    currency
  }
`;

export const ClubCurrencyContextUnauthenticatedFragment = graphql`
  fragment ClubCurrencyContextUnauthenticatedFragment on UnauthenticatedClub {
    currency
  }
`;
